import React, { useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { api } from "../../../lib/api";
import { act, useLoad } from "../../../lib/sales";
import { AnnotatedShot } from "./AnnotatedShot";
import { CaptureForm, UploadZone } from "./ShotInputs";

function Picker({ fid, step, onClose, onDone }) {
  const [rows] = useLoad("/admin/manual/media");
  const pick = async (s) => { if (await act(() => api.post(`/admin/manual/media/${s.shot_id}/attach`, { fid, step }), "Screenshot added to this step")) onDone(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl sm:w-[96vw] max-h-[90vh] overflow-y-auto" data-testid="shot-picker">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add a screenshot to step {step + 1}</DialogTitle></DialogHeader>
        <UploadZone fid={fid} step={step} onDone={onDone} compact />
        <CaptureForm pages={[]} fid={fid} step={step} onDone={onDone} />
        <p className="text-xs font-semibold">Or pick from the library</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">{(rows || []).map((s) => <button key={s.shot_id} onClick={() => pick(s)} className="text-left border border-[var(--line)] rounded-xl p-1.5 hover:border-[var(--gold)]" data-testid={`shot-pick-${s.shot_id}`}><AnnotatedShot s={s} /><p className="text-[11px] truncate mt-1">{s.title} · {s.status}</p></button>)}</div>
      </DialogContent>
    </Dialog>
  );
}

function Zoom({ s, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-6xl sm:w-[96vw] max-h-[94vh] overflow-y-auto" data-testid="shot-zoom">
        <DialogHeader><DialogTitle className="font-serif text-xl">{s.title}</DialogTitle></DialogHeader>
        <AnnotatedShot s={s} />
        {(s.caption || s.description) && <p className="text-sm">{s.caption}{s.description && <span className="block text-[#5c544b] text-xs mt-1">{s.description}</span>}</p>}
      </DialogContent>
    </Dialog>
  );
}

// Screenshots shown under one manual step; admins can add or remove them inline.
export function StepShots({ fid, step, shots, admin, reload }) {
  const [pick, setPick] = useState(false);
  const [zoom, setZoom] = useState(null);
  const mine = shots.filter((s) => s.step === step);
  if (!mine.length && !admin) return null;
  const detach = async (s) => { if (await act(() => api.post(`/admin/manual/media/${s.shot_id}/detach`, { fid, step }), "Removed from this step")) reload(); };
  return (
    <div className="flex flex-wrap gap-2 mt-1.5 mb-2" data-testid={`step-shots-${step}`}>
      {mine.map((s) => <div key={s.shot_id} className="relative w-full sm:w-[calc(50%-4px)] group">
        <button onClick={() => setZoom(s)} className="block w-full border border-[var(--line)] rounded-lg hover:border-[var(--gold)]" data-testid={`step-shot-${step}-${s.shot_id}`}><AnnotatedShot s={s} /></button>
        <p className="text-[11px] text-[#5c544b] mt-0.5">{s.caption || s.title}{s.status !== "approved" && <span className="ml-1 text-amber-700">({s.status} — hidden from staff)</span>}</p>
        {admin && <button onClick={() => detach(s)} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 border border-[var(--line)]" aria-label="Remove from step" data-testid={`step-shot-remove-${step}-${s.shot_id}`}><X className="w-3 h-3" /></button>}
      </div>)}
      {admin && <button onClick={() => setPick(true)} className="text-[11px] inline-flex items-center gap-1 text-[var(--gold)] hover:underline" data-testid={`step-add-shot-${step}`}><ImagePlus className="w-3.5 h-3.5" />Add screenshot</button>}
      {pick && <Picker fid={fid} step={step} onClose={() => setPick(false)} onDone={() => { setPick(false); reload(); }} />}
      {zoom && <Zoom s={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
